import { parameterById } from './registry';
import { apiTimestamp, utcDayRange } from './time';
import type { PlotSpec, WorkspaceState } from './types';

const HARMONIC_OPTIONS = ['nofundamental', 'perunit', 'interharmonics'] as const;

export interface PlotRequest {
  url: string;
  batchBase?: string;
  columns?: string[];
  range?: [number, number];
  cursor?: number;
}

export function canonicalHarmonicOptions(options: Record<string, boolean>): string[] {
  const enabled = new Set<string>();
  if (options.showFundamental === false) enabled.add('nofundamental');
  if (options.perUnit) enabled.add('perunit');
  if (options.showInterharmonics) enabled.add('interharmonics');
  return HARMONIC_OPTIONS.filter((item) => enabled.has(item));
}

export function effectiveRange(plot: PlotSpec, workspace: WorkspaceState): [number, number] {
  return plot.linkedTime || !plot.rangeOverride ? workspace.range : plot.rangeOverride;
}

export function effectiveCursor(plot: PlotSpec, workspace: WorkspaceState): number {
  return plot.linkedTime || plot.cursorOverride == null ? workspace.cursor : plot.cursorOverride;
}

export function buildPlotRequest(plot: PlotSpec, monitorId: string, workspace: WorkspaceState): PlotRequest {
  const parameter = parameterById(plot.parameterId);
  const range = effectiveRange(plot, workspace);
  const cursor = effectiveCursor(plot, workspace);
  const monitor = encodeURIComponent(monitorId);

  if (plot.kind === 'average-series' || plot.kind === 'phase-series') {
    const columns = plot.kind === 'phase-series' && parameter.phaseColumns ? parameter.phaseColumns : parameter.aggregateColumns;
    const batchBase = `/rms/${monitor}/from/${apiTimestamp(range[0])}/to/${apiTimestamp(range[1])}`;
    return {
      url: `${batchBase}/${columns.map(encodeURIComponent).join('/')}`,
      batchBase,
      columns: [...columns],
      range
    };
  }
  if (plot.kind === 'day-polar') {
    const day = utcDayRange(cursor);
    const columns = parameter.phaseColumns ?? parameter.aggregateColumns;
    const batchBase = `/rms/${monitor}/from/${apiTimestamp(day[0])}/to/${apiTimestamp(day[1])}`;
    return {
      url: `${batchBase}/${columns.map(encodeURIComponent).join('/')}`,
      batchBase,
      columns: [...columns],
      range: day,
      cursor
    };
  }
  if (plot.kind === 'heatmap') {
    return { url: `/heatmap/${monitor}/${encodeURIComponent(parameter.aggregateColumns[0])}`, cursor };
  }
  if (plot.kind === 'harmonic-trend') {
    const suffix = plot.options.perUnit ? '/perunit' : '';
    return {
      url: `/harmonicstrends/${monitor}/${parameter.harmonicType ?? 'current'}/from/${apiTimestamp(range[0])}/to/${apiTimestamp(range[1])}${suffix}`,
      range
    };
  }
  const type = parameter.harmonicType ?? 'current';
  const route = plot.kind === 'waveform' ? 'waveforms' : 'harmonics';
  const suffix = plot.kind === 'harmonic-spectrum' ? canonicalHarmonicOptions(plot.options).map((item) => `/${item}`).join('') : '';
  return {
    url: `/${route}/${monitor}/${type}/${apiTimestamp(cursor)}${suffix}/navigate`,
    cursor
  };
}
